import _ from 'lodash';
import urlJoin from 'url-join';
import EventEmitter from './EventEmitter';
import {PAGE_ID_PREFIX} from '../common/constants';
import {loadScript} from 'utils';

export default class PageLoader {
    constructor(locale, buildId, staticPath) {
        this.locale = locale;
        this.buildId = buildId;
        this.staticPath = staticPath;
        this.loadingRoutes = {};
        this.pageCache = {};
        this.loadEvents = new EventEmitter();
    }

    normalizeRoute(route) {
        if (route[0] !== '/') {
            throw new Error(`Route name should start with a "/", got "${route}"`);
        }

        return route.replace(/\/index$/, '/');
    }

    getPageLoadError(requestError, page, customMessage) {
        const error = new Error(customMessage || `Error when loading ${page} page`);
        error.statusCode = _.get(requestError, 'response.status') || 500;
        error.code = 'PAGE_LOAD_ERROR';
        return error;
    }

    loadPage(route) {
        const normalRoute = this.normalizeRoute(route);

        return new Promise((resolve, reject) => {
            const ready = async ({page, error}) => {
                delete this.loadingRoutes[normalRoute];
                this.loadEvents.off(normalRoute, ready);

                if (error) {
                    reject(error);
                } else {
                    resolve(page);
                }
            };

            const cachedPage = this.pageCache[normalRoute];
            if (cachedPage) {
                const {page, error} = cachedPage;
                if (error) {
                    reject(error);
                } else {
                    resolve(page);
                }
                return;
            }

            if (!this.loadingRoutes[normalRoute]) {
                this.loadEvents.on(normalRoute, ready);

                this.loadScript(normalRoute);
                this.loadingRoutes[normalRoute] = true;
            }
        });
    }

    registerPage(route, regFn) {
        const {page, error = null} = regFn();
        this.pageCache[route] = {page, error};

        this.loadEvents.emit(route, {page});
    }

    async loadScript(route) {
        const normalRoute = this.normalizeRoute(route);

        const scriptRoute = normalRoute.endsWith('/')
            ? normalRoute + 'index.js'
            : `${normalRoute}.js`;
        const scriptSrc = urlJoin(this.staticPath, this.buildId, 'pages', scriptRoute);
        const id = PAGE_ID_PREFIX + normalRoute;

        try {
            await loadScript(scriptSrc, {id});
        } catch {
            const error = new Error(`Error when loading js for route: ${normalRoute}`);
            error.statusCode = 404;
            error.code = 'SCRIPT_LOAD_ERROR';
            this.loadEvents.emit(normalRoute, {error});
        }
    }
}
